import React, { useState, useCallback, useEffect } from 'react';
import { FileBarChart, Send, Eye, Download, Loader, CheckCircle, Clock } from 'lucide-react';
import { buildLeaseStatementPdfBytes, bytesToBase64 } from '../utils/leaseStatement';

// Engine · Documents page — "Statements" panel.
//
// Generate a professional, branded statement from data the engine already holds
// and push it to a driver's portal "My Docs" tab. First (and currently only)
// statement type: a **Van Lease Statement** — the vehicle-lease charges applied to
// a driver's RCTIs up to a cutoff date. Figures come straight from the RCTI archive
// (getDriverRctis → deductions.leaseFee), so they match what the driver was charged.
//
// Rides entirely on existing plumbing: getDriverRctis (read) + the My Docs
// assignDocument push (a branded PDF filed to one driver, with a read receipt).
// Add further statement types here as the need arises.

const APPSSCRIPT = '/.netlify/functions/appsscript';
const DOCS_API   = '/.netlify/functions/driver-docs-sb';

const fmt = (n) => '$' + (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const leaseOf = (r) => Math.abs(Number(r?.deductions?.leaseFee) || 0);

function fmtDateLabel(iso) {
  const dt = new Date(iso);
  if (isNaN(dt)) return iso;
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}

// Statements filed via assignDocument all carry this title prefix — used to pull
// just the statements back out of the shared My Docs assignment store.
const STMT_PREFIX = 'Van Lease Statement';

// Issuer (Hollypaul Leasing) details — same source ContractsPage uses.
function issuerDetails() {
  try {
    const c = JSON.parse(localStorage.getItem('hp-company-details') || '{}');
    return {
      issuerName: c.assetCompany || 'Hollypaul Leasing Pty Ltd',
      issuerAbn:  c.assetAbn || '29 522 075 794',
    };
  } catch {
    return { issuerName: 'Hollypaul Leasing Pty Ltd', issuerAbn: '29 522 075 794' };
  }
}

export default function LeaseStatementBuilder({ drivers = [], addNotification }) {
  const [driverLogin, setDriver]  = useState('');
  const [driverFilter, setFilter] = useState('');
  const [cutoff, setCutoff]       = useState('2026-06-30');
  const [rctis, setRctis]         = useState(null);   // null = not loaded yet
  const [loading, setLoading]     = useState(false);
  const [busy, setBusy]           = useState(false);
  const [sent, setSent]           = useState([]);     // statements retained on the engine
  const [viewingId, setViewing]   = useState('');

  // Pull previously-sent statements back out of the My Docs assignment store, so a
  // copy of every statement we push stays visible (and re-openable) on the engine.
  const loadSent = useCallback(async () => {
    try {
      const res = await fetch(DOCS_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listAssignments' }),
      });
      const d = await res.json();
      if (d && d.success) setSent((d.documents || []).filter(r => (r.title || '').startsWith(STMT_PREFIX)));
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadSent(); }, [loadSent]);

  const filtered = (drivers || []).filter(d =>
    !driverFilter ||
    d.name?.toLowerCase().includes(driverFilter.toLowerCase()) ||
    d.loginName?.toLowerCase().includes(driverFilter.toLowerCase())
  ).slice(0, 8);

  const driverRec = (drivers || []).find(d => d.loginName === driverLogin) || null;

  const loadRctis = useCallback(async (login) => {
    setLoading(true); setRctis(null);
    try {
      const res = await fetch(APPSSCRIPT, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getDriverRctis', login }),
      });
      const d = await res.json();
      if (d && d.success) setRctis(d.rctis || []);
      else { setRctis([]); addNotification?.(d?.error || 'Could not load RCTIs', 'error'); }
    } catch (e) { setRctis([]); addNotification?.(e.message, 'error'); }
    finally { setLoading(false); }
  }, [addNotification]);

  const pick = (d) => { setDriver(d.loginName); setFilter(`${d.name || d.loginName} (${d.loginName})`); loadRctis(d.loginName); };

  // Start of a 4-weekly RCTI period, derived from its end (end − 27 days), in UTC to
  // avoid any timezone date-shift. Used so "up to <cutoff>" includes the period that
  // was *running* on the cutoff date (whose end falls after it), not just periods that
  // had already finished — e.g. a 14 Jun–11 Jul period still counts for a 30 Jun cutoff.
  const periodStartISO = (endISO) => {
    if (!endISO) return '';
    const d = new Date(endISO + 'T00:00:00Z');
    if (isNaN(d)) return endISO;
    d.setUTCDate(d.getUTCDate() - 27);
    return d.toISOString().slice(0, 10);
  };

  // Lease-charge rows for any period that had started by the cutoff, oldest first.
  const leaseRows = (rctis || [])
    .filter(r => leaseOf(r) > 0 && (periodStartISO(r.periodEndISO) || '') <= cutoff)
    .sort((a, b) => (a.periodEndISO || '').localeCompare(b.periodEndISO || ''))
    .map(r => ({
      periodLabel: r.periodLabel || '',
      invoiceNumber: r.invoiceNumber || '',
      periodEndISO: r.periodEndISO || '',
      leaseFee: leaseOf(r),
      leaseLabel: r.deductions?.leaseLabel || 'Van Lease Fee',
    }));
  const total = leaseRows.reduce((s, r) => s + r.leaseFee, 0);

  const firstRcti = (rctis || [])[0] || {};
  const title = `Van Lease Statement — up to ${fmtDateLabel(cutoff)}`;

  const buildData = () => {
    const { issuerName, issuerAbn } = issuerDetails();
    return {
      driverName:   firstRcti.personalName || driverRec?.name || driverLogin,
      driverEntity: firstRcti.companyName || firstRcti.personalName || driverRec?.name || driverLogin,
      driverAbn:    driverRec?.abn || '',
      issuerName, issuerAbn,
      cutoffLabel:  fmtDateLabel(cutoff),
      generatedOn:  new Date(),
      rows: leaseRows,
      total,
      coverageNote: 'This statement covers Recipient Created Tax Invoices archived from April 2026 onward.',
    };
  };

  const filename = `Van-Lease-Statement-${driverLogin}.pdf`;

  const preview = async () => {
    const w = window.open('', '_blank');
    try {
      const bytes = await buildLeaseStatementPdfBytes(buildData());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); addNotification?.(e.message, 'error'); }
  };

  const download = async () => {
    try {
      const bytes = await buildLeaseStatementPdfBytes(buildData());
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { addNotification?.(e.message, 'error'); }
  };

  const send = async () => {
    if (!driverLogin) { addNotification?.('Pick a driver', 'error'); return; }
    if (!leaseRows.length) { addNotification?.('No lease charges to send for this driver / period', 'error'); return; }
    setBusy(true);
    try {
      const bytes = await buildLeaseStatementPdfBytes(buildData());
      const res = await fetch(DOCS_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'assignDocument', driverLogin, category: 'Contracts',
          title, fileBase64: bytesToBase64(bytes), filename, contentType: 'application/pdf',
        }),
      });
      const d = await res.json();
      if (d && d.success) { addNotification?.(`Sent “${title}” to ${driverLogin}`, 'success'); loadSent(); }
      else addNotification?.(d?.error || 'Could not send', 'error');
    } catch (e) { addNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  // Re-open a retained statement from the engine copy (does not mark it read).
  const view = async (r) => {
    const w = window.open('', '_blank');
    setViewing(r.id);
    try {
      const res = await fetch(DOCS_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getAssignmentFile', id: r.id }),
      });
      const d = await res.json();
      if (!d || !d.success) { if (w) w.close(); addNotification?.(d?.error || 'Could not open', 'error'); return; }
      const url = URL.createObjectURL(b64ToBlob(d.base64, d.contentType));
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); addNotification?.(e.message, 'error'); }
    finally { setViewing(''); }
  };

  const inp = { width: '100%', padding: '9px 11px', fontSize: '0.85rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)' };
  const lbl = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)', marginBottom: 5, display: 'block' };

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <FileBarChart size={16} color="var(--hp-gold)" /> Statements — Van Lease Statement
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
        Builds a branded statement of the van-lease charges applied to a driver’s RCTIs up to a cutoff date, then pushes it to their “My Docs”. Figures come straight from the RCTI archive.
      </div>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 14, marginBottom: 14 }}>
        {/* Driver */}
        <div>
          <label style={lbl}>Driver</label>
          <input style={inp} placeholder="Search name or login…" value={driverFilter}
            onChange={e => { setFilter(e.target.value); setDriver(''); setRctis(null); }} />
          {driverFilter && !driverLogin && (
            <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 160, overflow: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 8, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>No match</div>
              ) : filtered.map(d => (
                <div key={d.loginName} onClick={() => pick(d)}
                  style={{ padding: '7px 10px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid var(--hp-border)' }}>
                  <strong>{d.name || d.loginName}</strong> <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{d.loginName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Cutoff */}
        <div>
          <label style={lbl}>Statement up to</label>
          <input type="date" style={inp} value={cutoff} onChange={e => setCutoff(e.target.value)} />
        </div>
      </div>

      {/* Preview of what will be on the statement */}
      {driverLogin && (
        <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 14 }}>
          {loading ? (
            <div style={{ padding: 16, fontSize: '0.82rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading RCTIs…
            </div>
          ) : leaseRows.length === 0 ? (
            <div style={{ padding: 16, fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
              No van-lease charges found on {driverRec?.name || driverLogin}’s archived RCTIs up to {fmtDateLabel(cutoff)}.
            </div>
          ) : (
            <>
              {leaseRows.map((r, i) => (
                <div key={r.invoiceNumber + r.periodEndISO} style={{ display: 'flex', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.8rem', background: i % 2 ? 'rgba(201,168,76,0.04)' : 'transparent' }}>
                  <span style={{ flex: 1 }}>{r.periodLabel}</span>
                  <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{r.invoiceNumber || '—'}</span>
                  <span style={{ width: 90, textAlign: 'right', fontWeight: 600 }}>{fmt(r.leaseFee)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10, padding: '10px 12px', fontSize: '0.85rem', fontWeight: 700, background: 'rgba(201,168,76,0.08)' }}>
                <span style={{ flex: 1 }}>Total — {leaseRows.length} period{leaseRows.length !== 1 ? 's' : ''}</span>
                <span style={{ width: 90, textAlign: 'right' }}>{fmt(total)}</span>
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={preview} disabled={!leaseRows.length}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Eye size={13} /> Preview
        </button>
        <button className="btn btn-secondary" onClick={download} disabled={!leaseRows.length}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={13} /> Download
        </button>
        <button className="btn btn-primary" onClick={send} disabled={busy || !leaseRows.length}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Send size={13} /> {busy ? 'Sending…' : 'Send to My Docs'}
        </button>
      </div>

      {/* Retained copies — every statement pushed stays on the engine, re-openable */}
      {sent.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ ...lbl, marginBottom: 8 }}>Retained on the engine · {sent.length}</div>
          <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {sent.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.8rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                  <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{r.driverLogin}</span> · sent {fmtWhen(r.assignedAt)}
                  </div>
                </div>
                {r.status === 'opened' ? (
                  <span style={{ color: 'var(--hp-green)', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <CheckCircle size={13} /> Opened {fmtWhen(r.openedAt)}
                  </span>
                ) : (
                  <span style={{ color: 'var(--hp-gold)', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <Clock size={13} /> Unopened
                  </span>
                )}
                <button onClick={() => view(r)} disabled={viewingId === r.id} title="View"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer', opacity: viewingId === r.id ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                  <Eye size={13} /> {viewingId === r.id ? '…' : 'View'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
